'use client'

import { autoUpdate, FloatingFocusManager, useDismiss, useFloating, useInteractions, useRole } from '@floating-ui/react'
import React, {
  createContext,
  forwardRef,
  Ref,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import mergeRefs from 'react-merge-refs'
import { CSSTransition } from 'react-transition-group'
import type { TransitionProps } from 'react-transition-group/Transition'
import cn from 'classnames'

import { stackingOrder } from 'src/shared/const/stackingOrder'
import { Backdrop as DefaultBackdrop } from 'src/shared/ui/Backdrop'
import { Portal } from 'src/shared/ui/Portal'
import { Stack } from 'src/shared/ui/Stack'
import { CloseIcon } from 'src/shared/icons'

import styles from './index.module.css'
import type { CommonModalProps } from './typing'
import { lockScroll, unlockScroll } from './utils'

/**
 * Контекст модального окна.
 *
 * @property parentRef - Референс на родительский элемент модалки.
 * @property componentRef - Референс на корневой элемент модалки.
 * @property setOpen - Функция для управления состоянием модалки (открыто/закрыто).
 */
type CommonModalContext = {
  parentRef: RefObject<HTMLDivElement | null>
  componentRef: RefObject<HTMLDivElement | null>
  setOpen?: (open: boolean) => void
}

const ModalContext = createContext<CommonModalContext>({
  parentRef: { current: null },
  componentRef: { current: null },
  setOpen: () => null,
})

export const Modal = forwardRef<HTMLDivElement, CommonModalProps>(
  (
    {
      open,
      onOpenChange,
      children,
      className,
      withClose = false,
      keepMounted,
      transitionProps = {},
      wrapperProps,
      contentProps,
      container,
      disableAutoFocus = false,
      disableFocusLock = false,
      disableBackdropClick = false,
      disableEscapeKeyDown = false,
      disableRestoreFocus = false,
      onMount,
      onUnmount,
      Backdrop = DefaultBackdrop,
      backdropProps,
      usePortal = true,
      zIndex = stackingOrder.MODAL,
      componentRef = null,
    },
    ref,
  ) => {
    const [exited, setExited] = useState<boolean | null>(null)

    const componentNodeRef = useRef<HTMLDivElement>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)

    const { refs, context } = useFloating({
      open,
      onOpenChange,
      whileElementsMounted: autoUpdate,
    })
    const interactions = useInteractions([
      useDismiss(context, {
        escapeKey: !disableEscapeKeyDown,
        outsidePress: !disableBackdropClick,
      }),
      useRole(context, { role: 'dialog' }),
    ])

    const isExited = exited || exited === null
    const shouldRender = open || keepMounted || !isExited

    const handleEntered = useCallback<Required<TransitionProps>['onEntered']>(
      (node, isAppearing) => {
        transitionProps.onEntered?.(node, isAppearing)
        onMount?.()
      },
      [onMount, transitionProps],
    )

    const handleExited = useCallback<Required<TransitionProps>['onExited']>(
      (node) => {
        setExited(true)
        transitionProps.onExited?.(node)
        onUnmount?.()
      },
      [onUnmount, transitionProps],
    )

    useEffect(() => {
      if (open && isExited) {
        lockScroll()

        setExited(false)
      }

      if (!open) unlockScroll()
    }, [open, isExited])

    useEffect(() => {
      if (disableAutoFocus || !open) return

      wrapperRef.current?.focus()
    }, [open, disableAutoFocus])

    const contextValue = useMemo<CommonModalContext>(
      () => ({
        parentRef: wrapperRef,
        componentRef: componentNodeRef,
        setOpen: onOpenChange,
      }),
      [onOpenChange],
    )

    const renderContent = () => (
      <Stack value={zIndex}>
        {(computedZIndex) => (
          <ModalContext.Provider value={contextValue}>
            <FloatingFocusManager
              context={context}
              initialFocus={disableAutoFocus ? undefined : 0}
              disabled={disableFocusLock || !open}
              returnFocus={!disableRestoreFocus}
            >
              <>
                {Backdrop && (
                  <Backdrop
                    className={styles.backdrop}
                    {...backdropProps}
                    open={open}
                    style={{ zIndex: computedZIndex }}
                  />
                )}
                <div
                  role="dialog"
                  {...wrapperProps}
                  ref={mergeRefs([ref, wrapperRef, wrapperProps?.ref as Ref<HTMLDivElement>])}
                  className={cn(styles.wrapper, wrapperProps?.className)}
                  tabIndex={-1}
                  style={{ zIndex: computedZIndex }}
                >
                  <CSSTransition
                    appear
                    timeout={200}
                    classNames={styles}
                    nodeRef={componentNodeRef}
                    in={open}
                    {...transitionProps}
                    onEntered={handleEntered}
                    onExited={handleExited}
                  >
                    <div
                      className={cn(styles.component, className)}
                      ref={mergeRefs([refs.setFloating, componentRef, componentNodeRef])}
                      {...interactions.getFloatingProps()}
                    >
                      {withClose && (
                        <div className={styles.close}>
                          <CloseIcon onClick={onOpenChange?.bind(null, false)} />
                        </div>
                      )}
                      <div {...contentProps} className={cn(styles.content, contentProps?.className)}>
                        {children}
                      </div>
                    </div>
                  </CSSTransition>
                </div>
              </>
            </FloatingFocusManager>
          </ModalContext.Provider>
        )}
      </Stack>
    )

    if (!shouldRender) return null

    return usePortal ? (
      <Portal getPortalContainer={container} immediateMount>
        {renderContent()}
      </Portal>
    ) : (
      renderContent()
    )
  },
)

Modal.displayName = 'Modal'
